import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  Flex,
  Box,
  ModalFooter
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import { FaTruck } from 'react-icons/fa'
import { IoIosAddCircle } from 'react-icons/io'

import useParametrizationConfigPurchase from '@hook/parametrization/use-config-purchase'
import useFilters from '@hook/shared/use-filters'

import Filter from '@component/Shared/Filter'

import paramsHelper from '@helper/params'
import { getCategoriasFornecedores } from '@service/ParametroCompra'

interface IValue {
  label: string
  value: string
}

const CronPurchaseModalUser: FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [usuario, setUsuario] = useState<IValue | null>(null)
  const { data } = useFilters()

  const [fornecedoresOriginal, setFornecedoresOriginal] = useState<any[]>([])
  const [fornecedoresOptions, setFornecedoresOptions] = useState<IValue[]>([])
  const [categoriasOptions, setCategoriasOptions] = useState<IValue[]>([])
  const { createUsers, companieId } = useParametrizationConfigPurchase()
  const [_fornecedoresPorCategoria, setFornecedoresPorCategoria] = useState<
    Record<string, IValue[]>
  >({})

  const [formParams, setFormParams] = useState<{
    fornecedorSelecionado: IValue[]
    categoria: IValue[]
  }>({
    fornecedorSelecionado: [],
    categoria: []
  })

  // Estado para controlar se algum select está aberto
  const [modalSize, setModalSize] = useState<'xl' | '4xl'>('xl')

  const handleMenuOpen = () => {
    setModalSize('4xl')
  }

  const handleMenuClose = () => {
    setModalSize('xl')
  }

  useEffect(() => {
    if (!isOpen) return

    const fetchFornecedores = async () => {
      try {
        const resp = await getCategoriasFornecedores() // ID da empresa
        setFornecedoresOriginal(resp.data?.data)

        // Aplica distinct nos fornecedores baseado no supplierId
        const fornecedoresUnicos = resp.data?.data?.filter(
          (f: any, index: number, array: any[]) =>
            array.findIndex((item: any) => item.supplierId === f.supplierId) ===
            index
        )

        const fornecedores = fornecedoresUnicos?.map((f: any) => ({
          label: f.supplierId + '-' + f.supplierDescription,
          value: String(f.supplierId)
        }))
        setFornecedoresOptions(fornecedores)
      } catch {
        setFornecedoresOptions([])
      }
    }

    fetchFornecedores()
  }, [isOpen])

  const handleFormChange = (
    field: keyof typeof formParams,
    value: IValue[]
  ) => {
    setFormParams(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleFornecedorSelecionadoChange = async (value: IValue) => {
    const valueS = [value]
    handleFormChange('fornecedorSelecionado', valueS)
    handleFormChange('categoria', [])

    if (valueS.length === 0) {
      setCategoriasOptions([])
      setFornecedoresPorCategoria({})
      return
    }

    try {
      const categoriasFiltradas: IValue[] = []
      const fornecedoresMap: Record<string, IValue[]> = {}

      fornecedoresOriginal.forEach(cat => {
        // Verifica se o supplier está entre os valores selecionados
        const isRelacionado = valueS.some(
          v => v.value === String(cat.supplierId)
        )

        if (isRelacionado) {
          const categoriaId = String(cat.compactCategoryId)

          // Adiciona a categoria se ainda não foi adicionada
          if (!categoriasFiltradas.some(c => c.value === categoriaId)) {
            categoriasFiltradas.push({
              label: cat.compactCategoryDescription,
              value: categoriaId
            })
          }

          // Inicializa array da categoria se necessário
          if (!fornecedoresMap[categoriaId]) {
            fornecedoresMap[categoriaId] = []
          }

          // Adiciona fornecedor relacionado à categoria
          fornecedoresMap[categoriaId].push({
            label: cat.supplierDescription,
            value: String(cat.supplierId)
          })
        }
      })

      setCategoriasOptions(categoriasFiltradas)
      setFornecedoresPorCategoria(fornecedoresMap)
      handleCategoriaChange(categoriasFiltradas)
    } catch {
      setCategoriasOptions([])
    }
  }

  const handleCategoriaChange = (value: IValue[]) => {
    handleFormChange('categoria', value)
  }

  const clearForm = () => {
    setFormParams({
      fornecedorSelecionado: [],
      categoria: []
    })
    setUsuario(null)
    setCategoriasOptions([])
    setFornecedoresPorCategoria({})
  }

  const validate = () => {
    const newErrors: string[] = []

    return newErrors.length === 0
  }

  // Validar se todos os campos obrigatórios estão preenchidos
  const isFormValid = () => {
    return (
      usuario && // Usuário selecionado
      formParams.fornecedorSelecionado.length > 0 && // Fornecedor selecionado
      formParams.categoria.length > 0 // Categoria compacta selecionada
    )
  }

  const [isSaving, setIsSaving] = useState(false)

  const handleSubmit = async () => {
    if (isSaving || !validate()) return
    setIsSaving(true)
    const success = await createUsers({
      fornecedorId: Number(formParams.fornecedorSelecionado[0].value),
      categoriasCompactasIds: paramsHelper.parseNumber(formParams.categoria),
      // Empresa selecionada no topo da tela (antes: 1000 fixo)
      empresaId: Number(companieId),

      usuarioId: usuario.value
    })
    setIsSaving(false)
    // Fecha só no sucesso; no erro o toast explica e o formulário continua preenchido
    if (success) {
      onClose()
      clearForm()
    }
  }

  return (
    <>
      <Button
        fontSize={14}
        size="sm"
        leftIcon={<IoIosAddCircle />}
        colorScheme="blue"
        variant="outline"
        onClick={onOpen}
      >
        Adicionar
      </Button>

      <Modal
        isCentered
        isOpen={isOpen}
        onClose={() => {
          onClose()
          clearForm()
        }}
        scrollBehavior="inside"
        motionPreset="slideInBottom"
        size={'xl'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            paddingTop={2}
            paddingBottom={2}
            paddingLeft={4}
            paddingRight={4}
            textColor={'gray.200'}
            background={'#1d5840'}
            borderTopLeftRadius={5}
            borderTopRightRadius={5}
            borderBottom="3px solid #ECC94B"
          >
            <Flex>
              <Box p={2}>
                <FaTruck />
              </Box>
              <Box p={2} pt={1}>
                Criar Comprador Cronograma
              </Box>
            </Flex>
          </ModalHeader>

          <ModalCloseButton />
          <ModalBody
            bg="white"
            pt={0}
            py={4}
            minH={modalSize === '4xl' ? '300px' : '300px'}
          >
            <FormControl isRequired>
              <Filter
                label="Usuário"
                options={data.users}
                isMulti={false}
                value={usuario ? [usuario] : []}
                onChange={value => {
                  const selected = Array.isArray(value) ? value[0] : value
                  setUsuario(selected as IValue)
                }}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
              />
            </FormControl>

            <br />

            <FormControl isRequired>
              <Filter
                label="Fornecedor"
                options={fornecedoresOptions}
                isMulti={false}
                value={formParams.fornecedorSelecionado}
                onChange={value => {
                  // eslint-disable-next-line no-void
                  void handleFornecedorSelecionadoChange(value as IValue)
                }}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
              />
            </FormControl>

            <br />

            <FormControl isRequired>
              <Filter
                label="Categoria Compacta"
                options={categoriasOptions}
                isMulti={true}
                isDisabled={formParams.fornecedorSelecionado.length === 0}
                value={formParams.categoria}
                onChange={value => handleCategoriaChange(value as IValue[])}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter
            bg="white"
            borderBottomLeftRadius={5}
            borderBottomRightRadius={5}
          >
            <Button
              colorScheme="blue"
              mr={3}
              onClick={() => {
                onClose()
                clearForm()
              }}
            >
              Fechar
            </Button>
            <Button
              bg="green.600"
              color="#fff"
              isDisabled={!isFormValid()}
              isLoading={isSaving}
              onClick={handleSubmit}
              _hover={!isFormValid() ? {} : { bg: 'green.500' }}
              _active={{ bg: 'green.500' }}
              _disabled={{ bg: 'gray.300', color: 'gray.500', _hover: {} }}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default CronPurchaseModalUser
